/**
 * Turns a rankings row into the flat model the console works with.
 *
 * `Candidate Info` is now single-cased: Name, Email, Phone, Location, always present
 * (null when unknown). The backend no longer emits the lowercase variants, so reading
 * `info.name` / `info.phone` returns undefined — hence the single reads below.
 */
import { RESUME_STATUS } from "./resumeStatus";

export const STAGE = {
  APPLIED: "applied",
  SCREENING: "screening",
  INTERVIEW: "interview",
};

/**
 * What we've decided about a candidate. Exactly one value, always — mirrors the Decision enum on
 * the server, which is the schema-level guarantee that a candidate can't be shortlisted AND
 * rejected at once.
 *
 * BACKLOG is a deliberate deferral, not an absence of a decision: UNDECIDED means nobody has
 * looked, BACKLOG means somebody looked and said "not now".
 */
export const DECISION = {
  UNDECIDED: "undecided",
  SHORTLISTED: "shortlisted",
  BACKLOG: "backlog",
  REJECTED: "rejected",
};

export const isDecided = (row) => row.decision !== DECISION.UNDECIDED;

const num = (v) => {
  const n = parseFloat(v);
  return Number.isNaN(n) ? null : n;
};

export const toRow = (ranking) => {
  const info = ranking["Candidate Info"] || {};
  const params = ranking.Parameters || {};

  return {
    resumeId: ranking["Resume Id"],
    rank: ranking["Resume Rank"],
    // A row exists from the moment its PDF is uploaded, so candidate_info can legitimately be
    // empty — the parse has not run yet. "Unknown" would be a poor thing to tell someone about a
    // file they dropped ten seconds ago; the filename is the only handle they have on it until
    // parsing finishes, and it is the handle they already recognise.
    name: info.Name || ranking.Filename || "Unknown",
    email: info.Email || "",
    phone: info.Phone || "",
    location: info.Location || "",
    total: num(ranking["Total Score"]),
    skills: num(params.skills_score),
    exp: num(params.work_experience_score),
    edu: num(params.education_score),
    p2p: num(ranking.p2p_score),
    screeningScheduled: ranking["Screening Scheduled"] === true,
    interviewSelected: ranking["Interview Selected"] === true,
    // One field, one value. An old payload with no Decision reads as undecided rather than
    // undefined, so the tab partition below can never drop a row on the floor.
    decision: ranking.Decision || DECISION.UNDECIDED,
    shortlisted: ranking.Decision === DECISION.SHORTLISTED,
    // Server-authoritative now; the client no longer derives this from two booleans.
    stage: ranking.Stage || STAGE.APPLIED,
    // Offset-bearing ISO strings, kept raw — the cell renders them in the viewer's zone (see
    // metaColumns.formatTimestamp). Null for any resume predating the timestamp migration, and for
    // any payload from a server that doesn't send them yet.
    createdAt: ranking["Created At"] || null,
    updatedAt: ranking["Updated At"] || null,
    // When a recruiter last acted on the decision — null until they have. Distinct from updatedAt,
    // which a background rescore bumps; see metaColumns.META_FIELDS.
    decidedAt: ranking["Decided At"] || null,
    // Where this resume is in the upload -> parse -> score pipeline. A row that predates the
    // status column reads as 'scored', which is what it is: it has a score, or it would not have
    // been visible at all before this change.
    status: ranking.Status || RESUME_STATUS.SCORED,
    isStale: ranking["Is Stale"] === true,
    error: ranking.Error || null,
    filename: ranking.Filename || null,
  };
};

/** Ported from Report.js — kept identical so avatars/initials don't change meaning. */
export const getInitials = (name) => {
  if (!name) return "N/A";
  return name
    .split(" ")
    .map((word) => word[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

export const formatScore = (score) =>
  score === null || score === undefined ? "—" : parseFloat(score).toFixed(1);

/**
 * Report.js rendered ↕ glyphs but never actually sorted. This is net-new.
 * Nulls always sink, regardless of direction, so unscored rows don't top the table.
 */
export const sortRows = (rows, key, dir) => {
  if (!key) return rows;
  return [...rows].sort((a, b) => {
    const x = a[key];
    const y = b[key];
    if (x === null && y === null) return 0;
    if (x === null) return 1;
    if (y === null) return -1;
    return dir === "asc" ? x - y : y - x;
  });
};

/**
 * The tabs are a PARTITION. Every candidate is in exactly one of review / screening / shortlist /
 * backlog / rejected, and "all" is their union — which is why All sits last: it's not a peer of
 * the others, it's the sum of them.
 *
 *   review     — undecided, never called.  The intake queue.
 *   screening  — undecided, called.        Still owes you a decision.
 *   shortlist / backlog / rejected         The three ways a decision can land.
 *
 * Both queues drain as decisions are made, and un-deciding drops a candidate back into whichever
 * queue they came from — automatically, because none of this is stored.
 *
 * The invariant worth defending: review + screening + shortlist + backlog + rejected === all.
 * There's a test. If these predicates ever overlap or leak, it fails.
 */
const PARTITION = {
  review: (r) => !isDecided(r) && !r.screeningScheduled,
  screening: (r) => !isDecided(r) && r.screeningScheduled,
  shortlist: (r) => r.decision === DECISION.SHORTLISTED,
  backlog: (r) => r.decision === DECISION.BACKLOG,
  rejected: (r) => r.decision === DECISION.REJECTED,
};

/** Screening holds the candidates who have been called and still owe a decision. */
export const isAwaitingScreeningDecision = PARTITION.screening;

export const filterByStage = (rows, stage) => {
  if (PARTITION[stage]) return rows.filter(PARTITION[stage]);
  // Behind INTERVIEWS_ENABLED, and deliberately NOT part of the partition: interviewSelected is an
  // orthogonal boolean, so an interview-selected candidate is also in one of the buckets above.
  // Resolve that before the flag goes on. See the decision-model spec.
  if (stage === "interview") return rows.filter((r) => r.interviewSelected);
  return rows;
};

/** Tab badges. Derived from the same predicates the tabs filter on, so they cannot disagree. */
export const countByStage = (rows) => ({
  all: rows.length,
  interview: rows.filter((r) => r.interviewSelected).length,
  ...Object.fromEntries(
    Object.entries(PARTITION).map(([stage, pred]) => [stage, rows.filter(pred).length])
  ),
});
